import { useEffect } from "react";
import {
  Database, Workflow, KeyRound, Cpu, ShieldOff, FileSearch, Globe2,
  ArrowRight, ShieldCheck, Mail, Lock,
} from "lucide-react";

const INK = "#0F1E2E";
const SUN = "#B45309";
const GOLD = "#FFC425";

const STEPS = [
  { n: "1", t: "Originates in the client's tenant", b: "A request starts inside the client's AWS environment — source data is read from their S3 / RDS / systems of record using scoped IAM roles." },
  { n: "2", t: "Encrypted with customer-managed keys", b: "Before a prompt leaves the VPC it's encrypted with a customer-managed KMS key. The client controls the key policy and can revoke access at any time." },
  { n: "3", t: "PrivateLink to Bedrock — inference, then back", b: "The request never touches the public internet. Claude runs in an AWS-operated environment segregated from Anthropic; the completion returns to the client's VPC." },
  { n: "4", t: "Audit trail in the client's CloudTrail", b: "Every invocation logs who called, what model, when, from which role — generated in, and owned by, the client's AWS account." },
];

const CONTROLS = [
  { Icon: KeyRound, t: "Customer-managed KMS keys", b: "Key policies name exactly which roles may encrypt or decrypt — auditable in the client's own console." },
  { Icon: FileSearch, t: "Audit logs the client owns", b: "CloudTrail data events for every model call live in the client's account — not a vendor's logging system." },
  { Icon: Globe2, t: "Region pinning + least privilege", b: "Data residency enforced by AWS per region; scoped IAM roles, no long-lived credentials, no shared accounts." },
];

export default function TrustPage() {
  useEffect(() => {
    document.title = "CompliSun · Security & Trust";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#FDFBF7", color: INK, fontFamily: "'Archivo', system-ui, -apple-system, sans-serif" }} data-testid="trust-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700;800;900&display=swap');
        .tp-h { font-weight: 800; letter-spacing: -0.02em; }
        .tp-eyebrow { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.2em; color: ${SUN}; }
        .tp-card { background: #fff; border: 1px solid rgba(15,30,46,0.1); border-radius: 16px; }
        ::-webkit-scrollbar { width: 12px; }
        ::-webkit-scrollbar-track { background: rgba(15,30,46,0.06) !important; }
        ::-webkit-scrollbar-thumb { background: rgba(15,30,46,0.35) !important; border-radius: 6px; border: 2px solid #fff; }
      `}</style>

      {/* Header */}
      <header className="border-b" style={{ borderColor: "rgba(15,30,46,0.08)", background: "#fff" }}>
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/brand/complisun-mark.png" alt="CompliSun" className="h-8 w-auto" />
            <span className="tp-h text-lg" style={{ color: INK }}>CompliSun</span>
            <span className="text-[10px] font-extrabold uppercase tracking-[0.18em] ml-1 px-2 py-1 rounded-full" style={{ background: `${SUN}12`, color: SUN }}>Trust Center</span>
          </div>
          <a href="mailto:colindarke@complisun.com?subject=Security%20review%20%E2%80%94%20CompliSun"
             className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-[13px] font-bold"
             style={{ background: INK, color: GOLD }} data-testid="trust-contact-btn">
            <Mail className="h-3.5 w-3.5" /> Contact security
          </a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 pb-20">
        {/* Hero + SOC 2 */}
        <section className="pt-14 md:pt-20">
          <div className="tp-eyebrow">Security &amp; trust · shareable with your security team</div>
          <h1 className="tp-h text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-3xl">
            Built for regulated data. <span style={{ color: SUN }}>Audited to prove it.</span>
          </h1>
          <p className="mt-5 text-lg leading-relaxed max-w-3xl" style={{ opacity: 0.75 }}>
            CompliSun runs compliance programs for banks and fintechs — so our own controls are held
            to the same standard we hold our clients to. This page summarizes our certification and
            the privacy-by-design architecture behind our AI features. Forward it to your security team.
          </p>

          <div className="mt-9 tp-card p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6" data-testid="trust-soc2-card">
            <img src="/brand/soc2-badge.png" alt="SOC 2 Type II compliant" className="h-32 w-auto flex-shrink-0" />
            <div>
              <div className="tp-h text-2xl">SOC 2 Type II compliant</div>
              <p className="text-[14px] leading-relaxed mt-2 max-w-xl" style={{ opacity: 0.72 }}>
                Our security, availability, and confidentiality controls are independently audited
                <strong> over time</strong> — a Type II examination, not a point-in-time design review.
              </p>
              <a href="mailto:colindarke@complisun.com?subject=SOC%202%20Type%202%20report%20request%20(under%20NDA)"
                 className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-full text-[13px] font-bold"
                 style={{ background: SUN, color: "#fff" }} data-testid="soc2-request-btn">
                Request the full report (under NDA) <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>

        {/* Architecture */}
        <section className="pt-14">
          <div className="tp-eyebrow">AI data privacy · closed-environment architecture</div>
          <h2 className="tp-h text-3xl md:text-4xl mt-3 max-w-3xl">
            Client data never leaves the client's cloud.
          </h2>
          <p className="mt-4 text-base md:text-lg leading-relaxed max-w-3xl" style={{ opacity: 0.72 }}>
            Our AI layer runs on AWS Bedrock <strong>inside the client's own AWS account</strong> — not on
            Claude Enterprise or ChatGPT Enterprise. Prompts and completions are never uploaded to
            Anthropic's systems, never used for model training, never reviewed by humans, and never retained.
          </p>

          <div className="mt-8 tp-card p-5 md:p-7" data-testid="trust-diagram">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-4 items-stretch">
              <div className="rounded-xl p-4 md:p-5" style={{ background: `${SUN}0a`, border: `2px solid ${SUN}55` }}>
                <div className="text-[10px] font-extrabold uppercase tracking-[0.18em] mb-3" style={{ color: SUN }}>
                  Client AWS account · client VPC
                </div>
                <div className="space-y-2.5">
                  {[[Database, "CMS & data sources", "S3, RDS, systems of record — stays in the client account at rest."],
                    [Workflow, "Workflow orchestrator", "Agentic logic and retrieval, running in the client subnet under client IAM."],
                    [KeyRound, "CloudTrail + KMS", "Client keys encrypt everything; every AI call logs to client CloudTrail."]].map(([Icon, t, b]) => (
                    <div key={t} className="rounded-lg p-3.5 flex items-start gap-3" style={{ background: "#fff", border: "1px solid rgba(15,30,46,0.12)" }}>
                      <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: SUN }} strokeWidth={1.8} />
                      <div>
                        <div className="text-[13px] font-bold">{t}</div>
                        <div className="text-[11.5px] leading-snug mt-0.5" style={{ opacity: 0.65 }}>{b}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex lg:flex-col items-center justify-center gap-2 py-1">
                <div className="text-center px-3 py-2 rounded-full text-[11px] font-extrabold uppercase tracking-[0.12em] whitespace-nowrap"
                     style={{ background: GOLD, color: INK }}>
                  AWS PrivateLink
                </div>
                <ArrowRight className="h-5 w-5 rotate-90 lg:rotate-0" style={{ color: SUN }} />
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-center max-w-[120px]" style={{ opacity: 0.55 }}>
                  AWS network only
                </div>
              </div>

              <div className="rounded-xl p-4 md:p-5" style={{ background: "rgba(15,30,46,0.03)", border: "2px dashed rgba(15,30,46,0.25)" }}>
                <div className="text-[10px] font-extrabold uppercase tracking-[0.18em] mb-3" style={{ opacity: 0.6 }}>
                  AWS Bedrock service environment · AWS-managed
                </div>
                <div className="rounded-lg p-4" style={{ background: "#fff", border: "1px solid rgba(15,30,46,0.12)" }}>
                  <div className="flex items-center gap-2.5 mb-2">
                    <Cpu className="h-4 w-4" style={{ color: SUN }} strokeWidth={1.8} />
                    <span className="text-[13px] font-bold">Claude on Bedrock</span>
                  </div>
                  <ul className="grid grid-cols-2 gap-1.5 text-[11.5px]" style={{ opacity: 0.72 }}>
                    {["Stateless inference", "No retention", "No training use", "No human review"].map((x) => (
                      <li key={x} className="flex items-center gap-1.5">
                        <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: SUN }} />{x}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-2.5 rounded-lg p-3.5 flex items-start gap-2.5" style={{ background: "#fff", border: "1px solid rgba(15,30,46,0.12)" }}>
                  <ShieldOff className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ opacity: 0.5 }} strokeWidth={1.8} />
                  <div className="text-[11.5px] leading-snug" style={{ opacity: 0.65 }}>
                    <strong>Anthropic — model weights provider only.</strong> Not provisioned to access
                    the deployment account, prompts, or completions.
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 text-center py-2 rounded-lg text-[11px] font-extrabold uppercase tracking-[0.2em]"
                 style={{ background: "rgba(15,30,46,0.05)", border: "1px solid rgba(15,30,46,0.1)", opacity: 0.85 }}>
              No public internet path — verifiable in the VPC route table
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map(({ n, t, b }) => (
              <div key={n} className="tp-card p-5 relative" data-testid={`trust-step-${n}`}>
                <div className="absolute top-3 right-4 tp-h text-3xl" style={{ color: `${SUN}20` }}>{n}</div>
                <div className="tp-h text-[15px] mb-1.5 pr-6">{t}</div>
                <p className="text-[12.5px] leading-relaxed" style={{ opacity: 0.7 }}>{b}</p>
              </div>
            ))}
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            {CONTROLS.map(({ Icon, t, b }) => (
              <div key={t} className="tp-card p-5 flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg grid place-items-center flex-shrink-0" style={{ background: `${SUN}12` }}>
                  <Icon style={{ color: SUN, height: 18, width: 18 }} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="tp-h text-[15px]">{t}</div>
                  <p className="text-[12.5px] leading-relaxed mt-1" style={{ opacity: 0.7 }}>{b}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Closing */}
        <section className="pt-12">
          <div className="tp-card p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5" style={{ background: INK, border: "none" }}>
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <Lock className="h-4 w-4" style={{ color: GOLD }} />
                <span className="text-[11px] font-extrabold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Have questions?</span>
              </div>
              <div className="tp-h text-xl" style={{ color: "#fff" }}>Security questionnaires, DDQs, and architecture reviews welcome.</div>
              <p className="text-[13px] mt-1" style={{ color: "rgba(255,255,255,0.7)" }}>
                Every control on this page is independently testable — key policies, CloudTrail logs, and VPC route tables.
              </p>
            </div>
            <a href="mailto:colindarke@complisun.com?subject=Security%20review%20%E2%80%94%20CompliSun"
               className="inline-flex items-center gap-2 h-11 px-6 rounded-full text-sm font-bold flex-shrink-0"
               style={{ background: GOLD, color: INK }} data-testid="trust-footer-cta">
              Talk to us <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <p className="mt-6 text-[11px] flex items-center gap-1.5" style={{ opacity: 0.5 }}>
            <ShieldCheck className="h-3.5 w-3.5" /> © CompliSun · SOC 2 Type II compliant · Full report and architecture documentation available under NDA.
          </p>
        </section>
      </main>
    </div>
  );
}
